import { Swiper } from "swiper";
import { Navigation } from "swiper/modules";

// ─── SVG constants ────────────────────────────────────────────────────────────

const IG_ICON_SVG = `<svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_10_1324)">
<path d="M16.0008 8.7998C12.0508 8.7998 8.80078 11.9998 8.80078 15.9998C8.80078 19.9498 12.0008 23.1998 16.0008 23.1998C19.9508 23.1998 23.1508 19.9998 23.1508 15.9998C23.1508 12.0498 19.9508 8.7998 16.0008 8.7998ZM16.0008 20.9498C13.3008 20.9498 11.0508 18.7498 11.0508 15.9998C11.0508 13.2998 13.2508 11.0498 16.0008 11.0498C18.7008 11.0498 20.9008 13.2498 20.9008 15.9998C20.9008 18.6998 18.7008 20.9498 16.0008 20.9498Z" fill="white"/>
<path d="M23.5 5.7998C22.5 5.7998 21.75 6.5998 21.75 7.5498C21.75 8.4998 22.55 9.2998 23.5 9.2998C24.45 9.2998 25.25 8.4998 25.25 7.5498C25.25 6.5998 24.5 5.7998 23.5 5.7998Z" fill="white"/>
<path d="M23.4504 0.899902H8.5504C4.30039 0.899902 0.900391 4.2999 0.900391 8.5499V23.4999C0.900391 27.6999 4.35039 31.1499 8.5504 31.1499H23.5004C27.7004 31.1499 31.1504 27.6999 31.1504 23.4999V8.5499C31.1504 4.2999 27.7004 0.899902 23.4504 0.899902ZM28.9004 23.4499C28.9004 26.4499 26.5004 28.8499 23.5004 28.8499H8.5504C5.5504 28.8499 3.15039 26.4499 3.15039 23.4499V8.5499C3.15039 5.5499 5.6004 3.1499 8.5504 3.1499H23.4504C26.4504 3.1499 28.8504 5.5999 28.8504 8.5499V23.4499H28.9004Z" fill="white"/>
</g>
<defs>
<clipPath id="clip0_10_1324">
<rect width="32" height="32" fill="white"/>
</clipPath>
</defs>
</svg>`;

const CAROUSEL_ICON_SVG = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M3 5h14v14H3zM7 1h14v14h-2V3H7z"/></svg>`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const escapeAttr = (s) =>
  String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const buildSlideHtml = (item, base) => {
  const poster = base + item.thumb;
  const anim = item.previewAnim ? base + item.previewAnim : null;
  const url = item.postUrl || "#";
  const isCarousel = item.postType === "carousel";
  const count = isCarousel ? item.slidesCount || 0 : 0;

  const videoTag = anim
    ? `<video class="reels-slider__video js-reels-video" muted playsinline preload="none" src="${escapeAttr(anim)}"></video>`
    : "";

  const countBadge =
    count > 1
      ? `<span class="reels-slider__count" aria-label="${count} slajdów">${CAROUSEL_ICON_SVG}${count}</span>`
      : "";

  return `<div class="reels-slider__slide swiper-slide">
    <a href="${escapeAttr(url)}" target="_blank" rel="noopener" class="reels-slider__link">
      <div class="reels-slider__media">
        <img class="reels-slider__poster" src="${escapeAttr(poster)}" alt="" loading="lazy" decoding="async">
        ${videoTag}
        ${countBadge}
        <span class="reels-slider__icon" aria-label="Instagram">${IG_ICON_SVG}</span>
      </div>
    </a>
  </div>`;
};

// ─── Auto-sequence (IntersectionObserver + visibility API) ────────────────────

const attachAutoSequence = (root, swiper) => {
  const slides = Array.from(root.querySelectorAll(".reels-slider__slide"));
  const total = slides.length;
  if (!total) return;

  let currentIndex = 0;
  let activeVideo = null;
  let endTimer = null;
  let running = false;
  let inView = false;

  // Returns the range of slide indices fully inside the swiper viewport.
  const getVisibleRange = () => {
    const swiperEl = root.querySelector(".swiper");
    const ai = swiper.activeIndex || 0;
    if (!swiperEl) return { first: ai, last: ai };
    const vr = swiperEl.getBoundingClientRect();
    const TOL = 2;
    let first = -1;
    let last = -1;
    for (let k = 0; k < slides.length; k++) {
      const sr = slides[k].getBoundingClientRect();
      const fullyVisible =
        sr.left >= vr.left - TOL && sr.right <= vr.right + TOL;
      if (fullyVisible) {
        if (first === -1) first = k;
        last = k;
      } else if (first !== -1) {
        break;
      }
    }
    if (first === -1) return { first: ai, last: ai };
    return { first, last };
  };

  // Pre-shift the carousel so the upcoming slide is fully visible before it plays.
  const ensureVisible = (i) => {
    const { first, last } = getVisibleRange();
    const visibleCount = Math.max(1, last - first + 1);
    const nextI = (i + 1) % total;
    if (i < first) {
      swiper.slideTo(i);
      return;
    }
    if (visibleCount >= 2 && nextI > last && nextI > i) {
      swiper.slideTo(Math.max(0, nextI - visibleCount + 1));
      return;
    }
    if (i > last) {
      swiper.slideTo(Math.max(0, i - visibleCount + 1));
    }
  };

  const stopCurrent = () => {
    if (activeVideo) {
      activeVideo.onended = null;
      activeVideo.pause();
      activeVideo.currentTime = 0;
      activeVideo.classList.remove("is-playing");
      activeVideo = null;
    }
    if (endTimer) {
      clearTimeout(endTimer);
      endTimer = null;
    }
  };

  const playIndex = (i) => {
    if (!running || !inView) return;
    stopCurrent();
    ensureVisible(i);
    const slide = slides[i];
    const video = slide.querySelector(".js-reels-video");
    const advance = () => {
      if (!running) return;
      currentIndex = (i + 1) % total;
      playIndex(currentIndex);
    };
    if (!video) {
      endTimer = setTimeout(advance, 1500);
      return;
    }
    video.currentTime = 0;
    video.classList.add("is-playing");
    activeVideo = video;
    video.onended = advance;
    // Safety fallback — allow some slack beyond the preview duration.
    endTimer = setTimeout(advance, 5000);
    const p = video.play();
    if (p && typeof p.catch === "function") {
      p.catch(() => {
        if (endTimer) clearTimeout(endTimer);
        endTimer = setTimeout(advance, 800);
      });
    }
  };

  const start = () => {
    if (running) return;
    running = true;
    playIndex(currentIndex);
  };

  const stop = () => {
    running = false;
    stopCurrent();
  };

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        const wasInView = inView;
        inView = e.isIntersecting;
        if (inView && !wasInView) start();
        else if (!inView && wasInView) stop();
      });
    },
    { threshold: 0.2 }
  );
  io.observe(root);

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else if (inView) start();
  });
};

// ─── Per-instance initialiser ─────────────────────────────────────────────────

const initReelsInstance = (root) => {
  const url = root.getAttribute("data-feed-url");
  const base = root.getAttribute("data-feed-base") || "";
  const wrapper = root.querySelector(".swiper-wrapper");
  if (!url || !wrapper) return;

  fetch(url, { mode: "cors" })
    .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
    .then((feed) => {
      const items = Array.isArray(feed.items) ? feed.items : [];
      if (!items.length) {
        root.hidden = true;
        return;
      }

      wrapper.insertAdjacentHTML(
        "beforeend",
        items.map((i) => buildSlideHtml(i, base)).join("")
      );

      const swiperEl = root.querySelector(".swiper-reels");
      if (!swiperEl) return;

      const swiper = new Swiper(swiperEl, {
        modules: [Navigation],
        loop: false,
        allowTouchMove: true,
        slidesPerView: 2,
        spaceBetween: 12,
        breakpoints: {
          576: { slidesPerView: 3, spaceBetween: 16 },
          992: { slidesPerView: 4, spaceBetween: 16 },
          1200: { slidesPerView: 5, spaceBetween: 20 },
        },
        navigation: {
          nextEl: ".swiper-reels-next",
          prevEl: ".swiper-reels-prev",
        },
      });

      attachAutoSequence(root, swiper);
    })
    .catch(() => {
      root.hidden = true;
    });
};

// ─── Entry point ──────────────────────────────────────────────────────────────

export default function initCarouselReels() {
  const roots = document.querySelectorAll(".reels-slider");
  roots.forEach(initReelsInstance);
}
